import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { toast } from "sonner";
import { Loader2, Smartphone } from "lucide-react";
import { InstallAppButton } from "@/components/InstallGuide";

export default function DispensarySignup() {
  const [form, setForm] = useState({
    email: "",
    password: "",
    business_name: "",
  });
  const [loading, setLoading] = useState(false);
  const { loginSuccess } = useApp();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  const pkg = params.get("pkg");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      loginSuccess(data);
      toast.success("Account created — welcome to Bud Link.");
      if (pkg) {
        navigate(`/pricing?pkg=${pkg}`);
      } else if (next) {
        navigate(next);
      } else {
        navigate("/dispensary/dashboard");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="eyebrow mb-2">For dispensaries</div>
          <h1 className="font-serif text-4xl">Start your free trial</h1>
          <p className="text-sm text-muted-foreground mt-2">
            30 days free. No card required to start.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="bg-card border border-border rounded-2xl p-7 space-y-4"
        >
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Business name
            </Label>
            <Input
              data-testid="signup-business-input"
              value={form.business_name}
              onChange={(e) =>
                setForm((f) => ({ ...f, business_name: e.target.value }))
              }
              required
              className="h-11 mt-1.5"
              placeholder="e.g. Curaleaf Center City"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Work email
            </Label>
            <Input
              data-testid="signup-email-input"
              type="email"
              value={form.email}
              onChange={(e) =>
                setForm((f) => ({ ...f, email: e.target.value }))
              }
              required
              className="h-11 mt-1.5"
              placeholder="owner@yourshop.com"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Password
            </Label>
            <Input
              data-testid="signup-password-input"
              type="password"
              value={form.password}
              onChange={(e) =>
                setForm((f) => ({ ...f, password: e.target.value }))
              }
              required
              minLength={8}
              className="h-11 mt-1.5"
              placeholder="At least 8 characters"
            />
          </div>

          <Button
            type="submit"
            data-testid="signup-submit"
            disabled={loading}
            className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
          </Button>
          <p className="text-center text-xs text-muted-foreground pt-2">
            Already have an account?{" "}
            <Link
              to="/dispensary/login"
              data-testid="signup-login-link"
              className="text-foreground underline underline-offset-4"
            >
              Sign in
            </Link>
          </p>
        </form>

        {/* Install on phone — easy access for owners on the go */}
        <div className="mt-6 rounded-2xl border border-border bg-card p-5">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-foreground text-background flex items-center justify-center shrink-0">
              <Smartphone className="w-4 h-4" strokeWidth={1.6} />
            </div>
            <div className="flex-1">
              <div className="font-serif text-base">
                Manage your menu on the go
              </div>
              <p className="text-xs text-muted-foreground mt-1 mb-3 leading-relaxed">
                Save Bud Link to your phone's home screen — one tap to update
                stock, prices, and pickup status from anywhere.
              </p>
              <InstallAppButton
                testId="signup-install-btn"
                label="Install Bud Link on your phone"
                variant="subtle"
                className="h-9 text-xs w-full sm:w-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
